import { jsPDF } from 'jspdf';

const safeText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  return JSON.stringify(value);
};

const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined) return '-';
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeText(value);
  return date.toLocaleString();
};

const ensureSpace = (doc, state, delta) => {
  const { pageHeight, bottomMargin } = state;
  if (state.cursorY + delta > pageHeight - bottomMargin) {
    doc.addPage();
    state.cursorY = state.topMargin;
  }
};

const addHeading = (doc, state, text) => {
  ensureSpace(doc, state, 28);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(text, state.leftMargin, state.cursorY);
  doc.setDrawColor(200);
  doc.line(
    state.leftMargin,
    state.cursorY + 6,
    state.pageWidth - state.rightMargin,
    state.cursorY + 6
  );
  state.cursorY += 18;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
};

const addKeyValue = (doc, state, label, value) => {
  ensureSpace(doc, state, 20);
  doc.setFont('helvetica', 'bold');
  doc.text(`${label}:`, state.leftMargin, state.cursorY);
  doc.setFont('helvetica', 'normal');
  doc.text(safeText(value), state.leftMargin + 110, state.cursorY);
  state.cursorY += 16;
};

const addTable = (doc, state, columns, rows) => {
  if (!rows.length) {
    ensureSpace(doc, state, 16);
    doc.setFont('helvetica', 'italic');
    doc.text('No records.', state.leftMargin, state.cursorY);
    doc.setFont('helvetica', 'normal');
    state.cursorY += 16;
    return;
  }

  const columnWidths = columns.map((col) => col.width || 100);
  const totalWidth = columnWidths.reduce((sum, w) => sum + w, 0);
  const available = state.pageWidth - state.leftMargin - state.rightMargin;
  const scale = totalWidth > available ? available / totalWidth : 1;
  const scaledWidths = columnWidths.map((w) => w * scale);

  const baseY = state.cursorY;
  ensureSpace(doc, state, 24);

  doc.setFont('helvetica', 'bold');
  columns.forEach((col, idx) => {
    doc.text(
      col.title,
      state.leftMargin + scaledWidths.slice(0, idx).reduce((sum, w) => sum + w, 0),
      state.cursorY
    );
  });
  state.cursorY += 14;
  doc.setFont('helvetica', 'normal');

  rows.forEach((row) => {
    let rowHeight = 0;
    columns.forEach((col, idx) => {
      const value = safeText(row[col.key]);
      const width = scaledWidths[idx];
      const lines = doc.splitTextToSize(value, width - 6);
      ensureSpace(doc, state, lines.length * 12 + 4);
      doc.text(
        lines,
        state.leftMargin + scaledWidths.slice(0, idx).reduce((sum, w) => sum + w, 0),
        state.cursorY
      );
      rowHeight = Math.max(rowHeight, lines.length * 12 + 4);
    });
    state.cursorY += rowHeight;
  });
};

const buildFilename = (agreement) => {
  const base = safeText(agreement?.title || agreement?.id || 'agreement')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${base || 'agreement'}-${timestamp}.pdf`;
};

export const generateAgreementPdf = ({
  agreement,
  items = [],
  invoices = [],
  payments = [],
  leadName = '',
  preparedBy = '',
  organization = 'Ravana Migration'
}) => {
  if (!agreement) {
    throw new Error('Agreement object is required to generate PDF');
  }

  const doc = new jsPDF({
    format: 'a4',
    unit: 'pt'
  });

  const state = {
    pageWidth: doc.internal.pageSize.getWidth(),
    pageHeight: doc.internal.pageSize.getHeight(),
    leftMargin: 48,
    rightMargin: 48,
    topMargin: 56,
    bottomMargin: 56,
    cursorY: 56
  };

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  // Header
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`${organization} — Agreement Summary`, state.leftMargin, state.cursorY);
  state.cursorY += 26;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${formatDate(new Date().toISOString())}`, state.leftMargin, state.cursorY);
  state.cursorY += 28;

  // Overview section
  addHeading(doc, state, 'Overview');
  addKeyValue(doc, state, 'Agreement Title', agreement.title || '-');
  addKeyValue(doc, state, 'Lead / Client', leadName || '-');
  addKeyValue(doc, state, 'Status', agreement.status || '-');
  addKeyValue(doc, state, 'Currency', agreement.currency || '-');
  addKeyValue(doc, state, 'Total Amount', formatCurrency(agreement.total_amount, agreement.currency));
  addKeyValue(doc, state, 'Prepared By', preparedBy || '-');
  addKeyValue(doc, state, 'Created At', formatDate(agreement.created_at));
  addKeyValue(doc, state, 'Last Updated', formatDate(agreement.updated_at));

  if (agreement.recipient_email) {
    addKeyValue(doc, state, 'Recipient Email', agreement.recipient_email);
  }

  // Items table
  addHeading(doc, state, 'Items');
  addTable(
    doc,
    state,
    [
      { key: 'name', title: 'Item', width: 200 },
      { key: 'type', title: 'Type', width: 80 },
      { key: 'quantity', title: 'Qty', width: 40 },
      { key: 'unit', title: 'Unit Price', width: 100 },
      { key: 'subtotal', title: 'Subtotal', width: 100 }
    ],
    items.map((item) => ({
      name: safeText(item.name || item.title || item.id),
      type: safeText(item.item_type || '-'),
      quantity: item.quantity ?? 1,
      unit: formatCurrency(item.unit_price, agreement.currency),
      subtotal: formatCurrency(item.subtotal, agreement.currency)
    }))
  );

  // Invoices
  addHeading(doc, state, 'Invoices');
  addTable(
    doc,
    state,
    [
      { key: 'due', title: 'Amount Due', width: 120 },
      { key: 'status', title: 'Status', width: 80 },
      { key: 'dueDate', title: 'Due Date', width: 100 },
      { key: 'updated', title: 'Updated', width: 140 }
    ],
    invoices.map((inv) => ({
      due: formatCurrency(inv.amount_due, agreement.currency),
      status: safeText(inv.status || 'pending'),
      dueDate: formatDate(inv.due_date),
      updated: formatDate(inv.updated_at || inv.created_at)
    }))
  );

  // Payments
  addHeading(doc, state, 'Payments');
  addTable(
    doc,
    state,
    [
      { key: 'amount', title: 'Amount', width: 120 },
      { key: 'method', title: 'Method', width: 100 },
      { key: 'reference', title: 'Reference', width: 120 },
      { key: 'paid', title: 'Paid At', width: 140 }
    ],
    payments.map((pay) => ({
      amount: formatCurrency(pay.amount, agreement.currency),
      method: safeText(pay.method || '-'),
      reference: safeText(pay.reference || '-'),
      paid: formatDate(pay.paid_at)
    }))
  );

  // Notes
  if (agreement.notes) {
    addHeading(doc, state, 'Notes');
    const noteLines = doc.splitTextToSize(agreement.notes, state.pageWidth - state.leftMargin - state.rightMargin);
    noteLines.forEach((line) => {
      ensureSpace(doc, state, 16);
      doc.text(line, state.leftMargin, state.cursorY);
      state.cursorY += 14;
    });
  }

  doc.save(buildFilename(agreement));
};

export default generateAgreementPdf;
